/**
 * Products + Selling rules module (prototype tabs "Products" and "Laws", plus the business-facts editor
 * the prototype kept on Review). Request/response shapes and the module's route table only; no logic.
 * Registered by `export * from './mod-products-laws.js';` in index.ts.
 */
import { z } from 'zod';
import { Id, IsoDate, Timestamp } from './common.js';
import { AllowedMime, MAX_UPLOAD_BYTES, PRESIGN_TTL_SECONDS, PresignDownloadResponse, PresignUploadResponse } from './misc.js';

const Text = (max: number) => z.string().max(max);
const VerticalQuery = z.object({ verticalId: Id });

// ---------------------------------------------------------------- products
export const ProductTier = z.object({
  name: z.string().trim().min(1).max(80),
  /** Whole INR as typed by the owner; null = "price on request". Never computed by a model. */
  priceInr: z.number().int().nonnegative().max(1_000_000_000).nullable(),
  note: Text(300).default(''),
}).strict();
export type ProductTier = z.infer<typeof ProductTier>;

export const Product = z.object({
  id: Id,
  verticalId: Id,
  key: z.string(),
  name: z.string(),
  stage: z.string(),
  link: z.string(),
  /** "Who it is for" (prototype: Audience). */
  audience: z.string(),
  /** One-breath positioning. */
  positioning: z.string(),
  /** Details and vocabulary every draft must respect. */
  details: z.string(),
  /** Brochure / literature text the drafts may quote. */
  brochureText: z.string(),
  tiers: z.array(ProductTier),
  proofPoints: z.array(z.string()),
  active: z.boolean(),
  /** Counted by the server. */
  vaultFilled: z.number().int().min(0).max(6),
  updatesCount: z.number().int().nonnegative(),
  filesCount: z.number().int().nonnegative(),
  lastUpdateOn: IsoDate.nullable(),
  updatedAt: Timestamp,
  version: z.number().int(),
});
export type Product = z.infer<typeof Product>;

export const CreateProductRequest = z.object({
  verticalId: Id,
  name: z.string().trim().min(1).max(160),
  stage: Text(160).optional(),
}).strict();
export type CreateProductRequest = z.infer<typeof CreateProductRequest>;

export const UpdateProductRequest = z.object({
  version: z.number().int(),
  name: z.string().trim().min(1).max(160).optional(),
  stage: Text(160).optional(),
  link: Text(500).optional(),
  audience: Text(1000).optional(),
  positioning: Text(2000).optional(),
  details: Text(6000).optional(),
  brochureText: Text(60_000).optional(),
  tiers: z.array(ProductTier).max(12).optional(),
  proofPoints: z.array(z.string().trim().min(1).max(500)).max(20).optional(),
  active: z.boolean().optional(),
}).strict();
export type UpdateProductRequest = z.infer<typeof UpdateProductRequest>;

export const ProductUpdate = z.object({ id: Id, productId: Id, date: IsoDate, text: z.string(), createdByName: z.string(), createdAt: Timestamp });
export type ProductUpdate = z.infer<typeof ProductUpdate>;
export const CreateProductUpdateRequest = z.object({ text: z.string().trim().min(1).max(2000) }).strict();

// ---------------------------------------------------------------- teaching vault
export const VAULT_KEYS = ['deb_oler', 'moat', 'unique_benefits', 'role_cards', 'choreography', 'counter_stories'] as const;
export const VaultKey = z.enum(VAULT_KEYS);
export type VaultKey = z.infer<typeof VaultKey>;
export const VaultFields = z.object({
  deb_oler: Text(6000),
  moat: Text(6000),
  unique_benefits: Text(6000),
  role_cards: Text(6000),
  choreography: Text(6000),
  counter_stories: Text(6000),
}).strict();
export type VaultFields = z.infer<typeof VaultFields>;
export const TeachingVault = z.object({
  productId: Id,
  fields: VaultFields,
  filled: z.number().int().min(0).max(6),
  updatedAt: Timestamp.nullable(),
});
export type TeachingVault = z.infer<typeof TeachingVault>;
export const PutTeachingVaultRequest = z.object({ fields: VaultFields }).strict();
/** AI proposals are never stored by the server; the owner edits then saves. null = AI could not help. */
export const VaultProposalResponse = z.object({ proposal: VaultFields.nullable(), filled: z.number().int().min(0).max(6) });
export type VaultProposalResponse = z.infer<typeof VaultProposalResponse>;
export const ParseVaultRequest = z.object({
  /** Text read from the uploaded document in the browser (txt, md, csv). */
  text: z.string().min(50, 'the document looks empty').max(60_000),
  fileName: z.string().trim().max(255).optional(),
}).strict();

// ---------------------------------------------------------------- company background (per business area)
/** The first this-many characters travel with every AI call (prototype rule). */
export const CONTEXT_ACTIVE_CHARS = 3000;
export const CompanyContext = z.object({
  verticalId: Id,
  text: z.string(),
  rawText: z.string(),
  chars: z.number().int().nonnegative(),
  activeChars: z.number().int().nonnegative(),
  updatedAt: Timestamp.nullable(),
});
export type CompanyContext = z.infer<typeof CompanyContext>;
export const PutCompanyContextRequest = z.object({
  verticalId: Id,
  text: z.string().max(60_000),
  /** Kept untouched when a distilled version is accepted. */
  rawText: z.string().max(200_000).optional(),
}).strict();
export const DistilContextRequest = z.object({ verticalId: Id, raw: z.string().min(1).max(200_000) }).strict();
export const DistilContextResponse = z.object({
  proposal: z.object({ text: z.string(), chars: z.number().int() }).nullable(),
  /** Set when no AI call was needed or possible (e.g. already short). */
  note: z.string().nullable(),
});
export type DistilContextResponse = z.infer<typeof DistilContextResponse>;

// ---------------------------------------------------------------- brochure files (private S3)
export const ProductPresignUploadRequest = z.object({
  fileName: z.string().trim().min(1).max(255).refine((n) => !/[\\/\u0000]/.test(n), 'no path separators'),
  mimeType: AllowedMime,
  sizeBytes: z.number().int().positive().max(MAX_UPLOAD_BYTES, 'max 20 MB'),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
}).strict();
export const ProductFile = z.object({
  id: Id,
  productId: Id,
  name: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  status: z.enum(['pending', 'uploaded']),
  createdAt: Timestamp,
});
export type ProductFile = z.infer<typeof ProductFile>;
export { PRESIGN_TTL_SECONDS as PRODUCT_FILE_TTL_SECONDS };

// ---------------------------------------------------------------- selling rules (laws)
export const LawOverrideMode = z.enum(['enable', 'disable']);
export const Law = z.object({
  id: Id,
  /** Rule code, e.g. L002 or O21. Shown only in small print / tooltips. */
  code: z.string(),
  /** Plain sentence-case title shown on screen. */
  title: z.string(),
  /** The prototype's original name (upper case, may use jargon). */
  originalName: z.string(),
  group: z.string(),
  engine: z.string(),
  introduced: z.string(),
  body: z.string(),
  scope: z.enum(['core', 'area']),
  /** Effective for this business area = core active AND no "disable" override. Computed by the server. */
  active: z.boolean(),
  override: z.object({ mode: LawOverrideMode, reason: z.string().nullable(), setByName: z.string(), setAt: Timestamp }).nullable(),
  note: z.string(),
  /** true when the owner reworded this rule for this area (setup module). */
  reworded: z.boolean().optional(),
  /** false for the restraint rules whose wording is fixed in every area. */
  adjustable: z.boolean().optional(),
});
export type Law = z.infer<typeof Law>;
export const LawListResponse = z.object({
  items: z.array(Law),
  groups: z.array(z.object({ name: z.string(), total: z.number().int(), active: z.number().int() })),
  canManage: z.boolean(),
});
export type LawListResponse = z.infer<typeof LawListResponse>;
export const SetLawActiveRequest = z.object({
  verticalId: Id,
  active: z.boolean(),
  reason: z.string().trim().max(500).optional(),
}).strict().refine((b) => b.active || (b.reason && b.reason.length >= 3), { message: 'Say why this rule is being switched off', path: ['reason'] });
export const SetLawNoteRequest = z.object({ verticalId: Id, note: z.string().max(2000) }).strict();

// ---------------------------------------------------------------- business facts (per business area)
export const BIZ_FACT_FIELDS = [
  { key: 'team', label: 'Team and roles', placeholder: 'Who sells, who builds, who supports' },
  { key: 'cash90', label: 'Cash covers the next 90 days?', placeholder: 'yes / borderline / no, and as of which date' },
  { key: 'burn', label: 'Monthly spend (₹)', placeholder: 'e.g. 1,20,000' },
  { key: 'hoursMonth', label: 'Selling hours this month so far', placeholder: 'e.g. 42' },
  { key: 'liPosts', label: 'LinkedIn posts published this month', placeholder: 'e.g. 7' },
  { key: 'inboundSources', label: 'New enquiries this month, by source', placeholder: 'e.g. 1 website form, 1 LinkedIn, 5 personal network' },
  { key: 'positioning', label: 'What we do, in one sentence', placeholder: 'What a buyer should believe we do' },
] as const;
export const BizFactKey = z.enum(['team', 'cash90', 'burn', 'hoursMonth', 'liPosts', 'inboundSources', 'positioning']);
export const BIZ_LOG_TYPES = [
  { key: 'market', label: 'Market or competitor event' },
  { key: 'message', label: 'Change in how we describe ourselves' },
  { key: 'asset', label: 'Material someone asked for' },
  { key: 'forecast', label: 'Revenue estimate or forecast note' },
  { key: 'ceo', label: 'Other owner fact' },
] as const;
export const BizLogType = z.enum(['market', 'message', 'asset', 'forecast', 'ceo']);
export const BusinessFacts = z.object({
  verticalId: Id,
  facts: z.record(BizFactKey, z.string()),
  updatedOn: IsoDate.nullable(),
  /** Keys still empty that the review advisors need (cash cover, selling hours). Computed by the server. */
  missingImportant: z.array(BizFactKey),
});
export type BusinessFacts = z.infer<typeof BusinessFacts>;
export const PutBusinessFactsRequest = z.object({ verticalId: Id, facts: z.record(BizFactKey, z.string().max(1000)) }).strict();
export const BusinessLogEntry = z.object({ id: Id, date: IsoDate, type: BizLogType, text: z.string(), createdByName: z.string() });
export type BusinessLogEntry = z.infer<typeof BusinessLogEntry>;
export const CreateBusinessLogRequest = z.object({ verticalId: Id, type: BizLogType, text: z.string().trim().min(1).max(1000) }).strict();

// ---------------------------------------------------------------- starter content
export const SeedStarterRequest = z.object({ verticalId: Id }).strict();
export const SeedStarterResponse = z.object({
  lawsAdded: z.number().int(),
  productsAdded: z.number().int(),
  contextSet: z.boolean(),
});

// ---------------------------------------------------------------- route table (prefix /api/v1)
type Def = { method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; path: string; query?: z.ZodTypeAny; body?: z.ZodTypeAny; response: z.ZodTypeAny };
const d = <T extends Def>(x: T): T => x;
const Ok = z.object({ ok: z.literal(true) });

export const productsLawsRoutes = {
  listProducts: d({ method: 'GET', path: '/products', query: VerticalQuery.extend({ includeArchived: z.coerce.boolean().optional() }), response: z.object({ items: z.array(Product) }) }),
  createProduct: d({ method: 'POST', path: '/products', body: CreateProductRequest, response: Product }),
  getProduct: d({ method: 'GET', path: '/products/:id', response: Product }),
  updateProduct: d({ method: 'PATCH', path: '/products/:id', body: UpdateProductRequest, response: Product }),
  listProductUpdates: d({ method: 'GET', path: '/products/:id/updates', response: z.object({ items: z.array(ProductUpdate) }) }),
  addProductUpdate: d({ method: 'POST', path: '/products/:id/updates', body: CreateProductUpdateRequest, response: ProductUpdate }),
  getVault: d({ method: 'GET', path: '/products/:id/vault', response: TeachingVault }),
  putVault: d({ method: 'PUT', path: '/products/:id/vault', body: PutTeachingVaultRequest, response: TeachingVault }),
  draftVault: d({ method: 'POST', path: '/products/:id/vault/draft', response: VaultProposalResponse }),
  parseVault: d({ method: 'POST', path: '/products/:id/vault/parse', body: ParseVaultRequest, response: VaultProposalResponse }),
  listProductFiles: d({ method: 'GET', path: '/products/:id/files', response: z.object({ items: z.array(ProductFile) }) }),
  presignProductFile: d({ method: 'POST', path: '/products/:id/files/presign-upload', body: ProductPresignUploadRequest, response: PresignUploadResponse }),
  confirmProductFile: d({ method: 'POST', path: '/products/:id/files/:fileId/confirm', response: ProductFile }),
  downloadProductFile: d({ method: 'POST', path: '/products/:id/files/:fileId/presign-download', response: PresignDownloadResponse }),
  removeProductFile: d({ method: 'DELETE', path: '/products/:id/files/:fileId', response: Ok }),
  getContext: d({ method: 'GET', path: '/company-context', query: VerticalQuery, response: CompanyContext }),
  putContext: d({ method: 'PUT', path: '/company-context', body: PutCompanyContextRequest, response: CompanyContext }),
  distilContext: d({ method: 'POST', path: '/company-context/distil', body: DistilContextRequest, response: DistilContextResponse }),
  listLaws: d({ method: 'GET', path: '/laws', query: VerticalQuery, response: LawListResponse }),
  setLawActive: d({ method: 'POST', path: '/laws/:id/active', body: SetLawActiveRequest, response: Law }),
  setLawNote: d({ method: 'PUT', path: '/laws/:id/note', body: SetLawNoteRequest, response: Law }),
  exportLaws: d({ method: 'GET', path: '/laws/export', query: VerticalQuery, response: z.string() }),
  getBusinessFacts: d({ method: 'GET', path: '/business-facts', query: VerticalQuery, response: BusinessFacts }),
  putBusinessFacts: d({ method: 'PUT', path: '/business-facts', body: PutBusinessFactsRequest, response: BusinessFacts }),
  listBusinessLog: d({ method: 'GET', path: '/business-log', query: VerticalQuery, response: z.object({ items: z.array(BusinessLogEntry) }) }),
  addBusinessLog: d({ method: 'POST', path: '/business-log', body: CreateBusinessLogRequest, response: BusinessLogEntry }),
  removeBusinessLog: d({ method: 'DELETE', path: '/business-log/:id', response: Ok }),
  seedStarter: d({ method: 'POST', path: '/products-laws/seed', body: SeedStarterRequest, response: SeedStarterResponse }),
} as const;
export type ProductsLawsRouteName = keyof typeof productsLawsRoutes;
